/* eslint-disable no-console */
import pino from 'pino';

// Initialize pino logger with pretty printing in development
const isDevelopment = process.env.NODE_ENV !== 'production';
const logLevel = 'info';

const pinoLogger = pino({
  level: logLevel,
  transport: isDevelopment
    ? {
        target: 'pino-pretty',
        options: {
          colorize: true,
          translateTime: 'yyyy-mm-dd HH:MM:ss',
          ignore: 'pid,hostname',
          singleLine: false,
          messageFormat: '{msg}',
        },
      }
    : undefined,
  formatters: {
    level: (label) => {
      return { level: label };
    },
  },
});

export function empty() {
  console.log();
}
export function divider() {
  console.log('='.repeat(80));
}

let prefix = '';
export function setPrefix(newPrefix) {
  prefix = newPrefix;
}

function formatArgument(arg) {
  if (typeof arg === 'object') {
    try {
      if (arg instanceof Error && arg.stack) {
        return arg.stack;
      }

      return JSON.stringify(arg, null, 2);
    } catch {
      // ...
    }
  }

  return arg;
}

/**
 * @returns {import('./logging-types').LoggingInterface}
 */
function buildLogger(tenantIdentifier, emoji, callFunc, pinoLevel) {
  const prefixParts = [];
  if (tenantIdentifier) {
    prefixParts.push(`[${tenantIdentifier}]`);
  }
  if (emoji) {
    prefixParts.push(emoji);
  }

  const logPrefix = prefixParts.filter(Boolean).join(' ').trim();

  const execute = function (caller, message, ...args) {
    const text = !logPrefix ? message : `${logPrefix}  ${message}`;
    const content = !prefix ? text : `${prefix} | ${text}`;

    // Use pino for structured logging if available
    if (pinoLevel && pinoLogger[pinoLevel]) {
      const logData = {};

      // Add all additional args as context
      args.forEach((arg, index) => {
        if (typeof arg === 'object' && !(arg instanceof Error)) {
          Object.assign(logData, arg);
        } else if (arg instanceof Error) {
          logData.error = {
            message: arg.message,
            stack: arg.stack,
            name: arg.name,
          };
        } else {
          logData[`arg${index}`] = arg;
        }
      });

      pinoLogger[pinoLevel](logData, content);
    } else {
      // Fallback to console for methods without pino equivalent
      caller(content, args.map(formatArgument).join('\n'));
    }
  };

  const loggerMethod = (...args) => execute(callFunc, ...args);
  loggerMethod.trace = (...args) => execute(console.trace, ...args);

  return loggerMethod;
}

/**
 * @type {Record<string, {
 *  log: import('./logging-types').LoggingInterface
 *  debug: import('./logging-types').LoggingInterface
 *  info: import('./logging-types').LoggingInterface
 *  success: import('./logging-types').LoggingInterface
 *  warning: import('./logging-types').LoggingInterface
 *  error: import('./logging-types').LoggingInterface
 *  fatal: import('./logging-types').LoggingInterface
 *  critical: import('./logging-types').LoggingInterface
 * }>}
 */
const loggersByTenant = {};

function buildLoggersWithTenant(tenantIdentifier) {
  const tenant = tenantIdentifier || '$_NoTenantIdentifier_$';
  if (!loggersByTenant[tenant]) {
    loggersByTenant[tenant] = {
      log: buildLogger(tenantIdentifier, null, console.log, 'info'),
      debug: buildLogger(tenantIdentifier, '🔍', console.debug, 'debug'),
      info: buildLogger(tenantIdentifier, 'ℹ️', console.info, 'info'),
      success: buildLogger(tenantIdentifier, '✅', console.log, 'info'),
      warning: buildLogger(tenantIdentifier, '⚠️', console.warn, 'warn'),
      error: buildLogger(tenantIdentifier, '❌', console.error, 'error'),
      fatal: buildLogger(tenantIdentifier, '💣', console.error, 'fatal'),
      critical: buildLogger(tenantIdentifier, '🔥', console.error, 'error'),
    };
  }

  return loggersByTenant[tenant];
}

const defaultLoggers = buildLoggersWithTenant(null);
export const withTenant = buildLoggersWithTenant;

export const log = defaultLoggers.log;
export const debug = defaultLoggers.debug;
export const info = defaultLoggers.info;
export const success = defaultLoggers.success;
export const warning = defaultLoggers.warning;
export const warn = defaultLoggers.warning; // Alias for backward compatibility
export const error = defaultLoggers.error;
export const fatal = defaultLoggers.fatal;
export const critical = defaultLoggers.critical;

/**
 * @param {Error} errorInstance
 */
export function exception(errorInstance, details = null) {
  let message = `Exception [${errorInstance.message}]`;
  if (/sequelize/gim.test(errorInstance.name)) {
    let messageWithSql = `${errorInstance.name}, ${errorInstance.message}`;
    if (errorInstance.sql) {
      messageWithSql += `>>> SQL: ${errorInstance.sql} <<<`;
    }

    message = `Database Exception [${messageWithSql}]:`;
  }

  if (errorInstance.stack) {
    if (details) {
      defaultLoggers.fatal(message, errorInstance, details);
    } else {
      defaultLoggers.fatal(message, errorInstance);
    }
  } else {
    if (details) {
      defaultLoggers.fatal.trace(message, errorInstance, details);
    } else {
      defaultLoggers.fatal.trace(message, errorInstance);
    }
  }
}
